import { Typography } from '@euler/app/components/typography';
import { aiColors, tint } from '@euler/app/flows/ai-suite/theme';
import { BiTag } from '@euler/app/flows/bi-dashboards/components/ui/BiTag';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { BiUploadSheetView } from '@euler/model/bi/upload';
import { Ban, Check, History } from 'lucide-react-native';
import { memo } from 'react';
import { Pressable, View } from 'react-native';
import { biRowsColumns, biSheetBlockText } from '../functions/biUploadLabels';

/**
 * One sheet of the file: name, size and its first headers, to recognize
 * it. A blocked sheet says why and cannot be ticked; a sheet imported from
 * this file before is marked so.
 */
export const SheetRow = memo(
  ({
    sheet,
    rowLimit,
    selected,
    disabled,
    onToggle,
  }: {
    sheet: BiUploadSheetView;
    rowLimit: number | null;
    selected: boolean;
    disabled: boolean;
    onToggle: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const blocked = !!sheet.block;
    const inactive = disabled || blocked;
    return (
      <Pressable
        accessibilityRole="checkbox"
        accessibilityLabel={`工作表「${sheet.name}」`}
        accessibilityState={{ checked: selected, disabled: inactive }}
        disabled={inactive}
        onPress={onToggle}
        style={({ pressed }) => ({
          flexDirection: 'row',
          alignItems: 'flex-start',
          gap: rpx(10),
          padding: rpx(12),
          borderRadius: rpx(11),
          borderWidth: 1,
          borderColor: selected ? aiColors.primary : aiColors.border,
          backgroundColor: selected
            ? tint(aiColors.primary, 0.05)
            : pressed
            ? aiColors.muted
            : aiColors.card,
          opacity: blocked ? 0.6 : disabled ? 0.75 : 1,
        })}
      >
        <View
          style={{
            width: rpx(20),
            height: rpx(20),
            marginTop: rpx(1),
            borderRadius: rpx(6),
            borderWidth: 1.5,
            borderColor: selected ? aiColors.primary : aiColors.border,
            backgroundColor: selected ? aiColors.primary : aiColors.card,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {selected ? (
            <Check
              size={13}
              color={aiColors.primaryForeground}
              strokeWidth={3}
            />
          ) : blocked ? (
            <Ban size={12} color={aiColors.mutedForeground} />
          ) : null}
        </View>
        <View style={{ flex: 1, gap: rpx(4) }}>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: rpx(6),
            }}
          >
            <Typography.Text
              medium
              size={rpx(14)}
              color={aiColors.foreground}
              numberOfLines={1}
              style={{ flexShrink: 1 }}
            >
              {sheet.name}
            </Typography.Text>
            {sheet.block ? (
              <BiTag
                text={biSheetBlockText(sheet.block, rowLimit)}
                color={aiColors.destructive}
              />
            ) : null}
            {sheet.alreadyImported ? (
              <BiTag text="已导入过" color={aiColors.amber} icon={History} />
            ) : null}
          </View>
          <Typography.Text size={rpx(12)} color={aiColors.mutedForeground}>
            {biRowsColumns(sheet.rowCount, sheet.columnCount)}
          </Typography.Text>
          {sheet.headers.length ? (
            <Typography.Text
              size={rpx(12)}
              color={aiColors.mutedForeground}
              numberOfLines={1}
            >
              {sheet.headers.join('、')}
              {sheet.columnCount > sheet.headers.length ? '…' : ''}
            </Typography.Text>
          ) : null}
        </View>
      </Pressable>
    );
  },
);
